import React, { useEffect, useRef, useState } from "react";
import { Loader2, Check, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

const DEBOUNCE_MS = 800;

/**
 * Right-rail notes panel with 800ms debounced auto-save.
 * Locked once the session is no longer in {draft, active}.
 */
export const NotesPanel = ({ sessionId, initialNotes, locked }) => {
    const [text, setText] = useState(initialNotes ?? "");
    const [status, setStatus] = useState("idle"); // idle | saving | saved | error
    const debounceRef = useRef(null);
    const lastSavedRef = useRef(initialNotes ?? "");

    // If parent re-fetches a session and notes changed server-side, sync once.
    useEffect(() => {
        if (initialNotes !== undefined && initialNotes !== lastSavedRef.current && initialNotes !== text) {
            setText(initialNotes);
            lastSavedRef.current = initialNotes;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialNotes]);

    const onChange = (e) => {
        if (locked) return;
        const v = e.target.value;
        setText(v);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            if (v === lastSavedRef.current) return;
            setStatus("saving");
            try {
                await api.patch(`/sessions/${sessionId}/notes`, { student_notes: v });
                lastSavedRef.current = v;
                setStatus("saved");
                setTimeout(() => setStatus((s) => (s === "saved" ? "idle" : s)), 1500);
            } catch (err) {
                setStatus("error");
            }
        }, DEBOUNCE_MS);
    };

    return (
        <div className="flex h-full flex-col gap-3" data-testid="notes-panel">
            <div className="flex items-center justify-between">
                <Label htmlFor="student_notes" className="text-sm font-semibold text-foreground">
                    Your notes
                </Label>
                <NotesStatus locked={locked} status={status} />
            </div>
            <Textarea
                id="student_notes"
                value={text}
                onChange={onChange}
                placeholder={locked ? "Notes are locked — session has ended." : "Jot down observations, hypotheses, or things you want to follow up on."}
                disabled={locked}
                className={cn(
                    "min-h-[400px] flex-1 resize-none border-border/80 bg-surface text-sm leading-relaxed",
                    locked && "bg-muted/40 cursor-not-allowed"
                )}
                data-testid="notes-textarea"
            />
        </div>
    );
};

const NotesStatus = ({ locked, status }) => {
    if (locked) {
        return (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid="notes-status-locked">
                <Lock className="h-3 w-3" />
                locked
            </span>
        );
    }
    if (status === "saving") {
        return (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid="notes-status-saving">
                <Loader2 className="h-3 w-3 animate-spin" />
                saving…
            </span>
        );
    }
    if (status === "saved") {
        return (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-600" data-testid="notes-status-saved">
                <Check className="h-3 w-3" />
                saved
            </span>
        );
    }
    if (status === "error") {
        return <span className="text-xs text-destructive" data-testid="notes-status-error">save failed</span>;
    }
    return null;
};
